import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { collectRoles, hasRole } from '../utils/roles';
import { apiUrl } from '../config/api';

const PROTECTED_USER_IDS = new Set(['1001','1002']);

const pageWrapper = { boxSizing:'border-box', padding:'24px 20px', width:'100%', maxWidth:'100%', margin:'0 auto', overflowX:'hidden' };
const tableWrapper = { boxSizing:'border-box', width:'100%', maxWidth:'100%', border:'1px solid #e2e8f0', borderRadius:12, boxShadow:'0 3px 10px rgba(0,0,0,0.05)', background:'#fff', overflowX:'auto', WebkitOverflowScrolling:'touch' };
const tableStyle = { width:'100%', borderCollapse:'separate', borderSpacing:0, tableLayout:'fixed' };
const tableHeaderStyle = { padding:'10px 12px', fontSize:12, textTransform:'uppercase', letterSpacing:'0.5px', textAlign:'left', borderBottom:'1px solid #e2e8f0', color:'#475569' };
const filterInputStyle = { width:'100%', padding:'4px 6px', border:'1px solid #cbd5f5', borderRadius:6, fontSize:13, background:'#fff' };
const cellStyle = { padding:'9px 12px', borderBottom:'2px solid #dbeafe', verticalAlign:'top', wordBreak:'break-word' };
const journalLinkStyle = { color:'#1d4ed8', textDecoration:'underline', fontWeight:600 };
const statusCellStyle = { display:'flex', flexWrap:'wrap', gap:4, alignItems:'center' };
const statusChipBase = { display:'inline-flex', alignItems:'center', gap:3, padding:'2px 6px', borderRadius:6, fontSize:10, fontWeight:600, lineHeight:1.2 };
const statusLabelStyle = { fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#475569' };
const statusTextStyle = { fontSize:10, fontWeight:500 };
const rowBaseStyle = { borderBottom:'2px solid #dbeafe', transition:'background 0.2s ease, border-color 0.2s ease' };
const quickFilterOptions = [
  { key:'all', label:'Все', type:'all', scope:null },
  { key:'gph', label:'Только ГПХ', type:'gph', scope:null },
  { key:'td', label:'Только ТД', type:'td', scope:null },
  { key:'divider-1', isDivider:true },
  { key:'active_all', label:'Все работающие', type:'all', scope:'active_all' },
  { key:'active_gph', label:'Работающие ГПХ', type:'gph', scope:'active_gph' },
  { key:'active_td', label:'Работающие ТД', type:'td', scope:'active_td' },
  { key:'dismissed', label:'Уволенные', type:'all', scope:'dismissed' },
];
const defaultQuickFilterKey = 'all';
const quickFilterFallbackOption = quickFilterOptions.find(option => !option.isDivider);
const quickFilterButtonStyle = {
  padding:'7px 14px',
  borderRadius:999,
  border:'1px solid transparent',
  background:'#f8fafc',
  color:'#0f172a',
  fontSize:13,
  fontWeight:600,
  cursor:'pointer',
  transition:'all 0.15s ease',
  boxShadow:'0 1px 2px rgba(15,23,42,0.15)'
};
const quickFilterButtonActiveStyle = {
  background:'#1d4ed8',
  color:'#fff',
  borderColor:'#1d4ed8',
  boxShadow:'0 6px 18px rgba(37,99,235,0.35)'
};
const filterDividerStyle = { width:1, height:28, background:'#e5e7eb' };
const quickFilterBadgeStyle = {
  marginLeft:6,
  padding:'1px 8px',
  borderRadius:999,
  fontSize:12,
  fontWeight:700,
  background:'rgba(15,23,42,0.1)',
  color:'#0f172a',
  lineHeight:1.4
};
const quickFilterBadgeActiveStyle = {
  background:'#fff',
  color:'#1d4ed8'
};
const quickFilterBadgeAlertStyle = {
  background:'#fee2e2',
  color:'#b91c1c'
};
const quickFilterBadgeAlertActiveStyle = {
  background:'#b91c1c',
  color:'#fff'
};

function normalizeDateValue(value){
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  dt.setHours(0,0,0,0);
  return dt;
}

function formatDate(value){
  const dt = normalizeDateValue(value);
  if (!dt) return '—';
  const dd = String(dt.getDate()).padStart(2,'0');
  const mm = String(dt.getMonth()+1).padStart(2,'0');
  const yyyy = dt.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function isRussianCitizen(value){
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'рф' || normalized === 'россия' || normalized === 'российская федерация';
}

function daysFromToday(value){
  const due = normalizeDateValue(value);
  if (!due) return null;
  const today = new Date();
  today.setHours(0,0,0,0);
  const diffMs = due.getTime() - today.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

function rowNeedsUrgentDocs(row){
  const needsMvd = !isRussianCitizen(row.citizenship);
  const startDate = row.contract_from || row.contract_date;
  const endDate = row.contract_to;
  const startDays = daysFromToday(startDate);
  const endDays = daysFromToday(endDate);
  const startDue = startDays !== null && startDays <= 2;
  const endDue = endDays !== null && endDays <= 2;
  const startPending = startDue && (row.start_send_docs !== true || (needsMvd && row.start_notify_mvd !== true));
  const endPending = endDue && (row.end_send_docs !== true || (needsMvd && row.end_notify_mvd !== true));
  return startPending || endPending;
}

export default function HrEmployeesPage(){
  const token = (typeof window!=='undefined') ? (localStorage.getItem('authToken')||'') : '';
  let user = null; let isSuper=false; let roles=[];
  try {
    user = JSON.parse(localStorage.getItem('authUser')||'null');
    isSuper = ['1001','1002'].includes(String(user?.id));
    roles = collectRoles(user);
  } catch {}
  const isAdmin = hasRole(roles, 'admin');
  const isHr = hasRole(roles, 'hr');
  const hrOnly = isHr && !isAdmin && !isSuper;
  const navigate = useNavigate();
  const authHeaders = useMemo(() => (token? { Authorization: `Bearer ${token}` } : {}), [token]);
  const jsonHeaders = useMemo(() => ({ ...authHeaders, 'Content-Type': 'application/json' }), [authHeaders]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState(defaultQuickFilterKey);
  const currentQuickFilter = useMemo(()=>{
    const found = quickFilterOptions.find(option => option.key === activeQuickFilter && !option.isDivider);
    return found || quickFilterFallbackOption;
  }, [activeQuickFilter]);
  const typeFilter = currentQuickFilter?.type || 'all';
  const scopeFilter = currentQuickFilter?.scope || null;
  const [selectedId, setSelectedId] = useState(null);
  const [showOnlyRed, setShowOnlyRed] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    full_name: '',
    position: '',
    work_object: '',
    contract_type: '',
    citizenship: '',
    registration_to: '',
    contract_number: '',
    contract_date: '',
    contract_to: '',
  });

  function handleFilterChange(field, value) {
    setFilters(f => ({ ...f, [field]: value }));
  }

  const filterRow = useCallback((row) => {
    if (filters.user_id && String(row.user_id||'').toLowerCase().indexOf(filters.user_id.toLowerCase()) === -1) return false;
    if (filters.full_name && String(row.full_name||'').toLowerCase().indexOf(filters.full_name.toLowerCase()) === -1) return false;
    if (filters.position && String(row.position||'').toLowerCase().indexOf(filters.position.toLowerCase()) === -1) return false;
    if (filters.work_object && String(row.work_object||'').toLowerCase().indexOf(filters.work_object.toLowerCase()) === -1) return false;
    if (filters.contract_type && (row.contract_type==='gph'?'ГПХ':'ТД').toLowerCase().indexOf(filters.contract_type.toLowerCase()) === -1) return false;
    if (filters.citizenship && String(row.citizenship||'').toLowerCase().indexOf(filters.citizenship.toLowerCase()) === -1) return false;
    if (filters.registration_to && String(row.registration_to||'').toLowerCase().indexOf(filters.registration_to.toLowerCase()) === -1) return false;
    if (filters.contract_number && String(row.contract_number||'').toLowerCase().indexOf(filters.contract_number.toLowerCase()) === -1) return false;
    if (filters.contract_date && String(row.contract_date||'').toLowerCase().indexOf(filters.contract_date.toLowerCase()) === -1) return false;
    if (filters.contract_to && String(row.contract_to||'').toLowerCase().indexOf(filters.contract_to.toLowerCase()) === -1) return false;
    return true;
  }, [filters]);

  const fetchList = useCallback(()=>{
    setLoading(true);
    const qs = typeFilter === 'all' ? '' : `?type=${typeFilter}`;
    fetch(apiUrl(`/api/employees${qs}`), { headers: authHeaders })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (ok){
          const rows = Array.isArray(data) ? data : [];
          setList(sortEmployeesById(rows));
        } else {
          alert('Ошибка: '+(data?.error||'server'));
        }
      })
      .catch(()=> {})
      .finally(()=> setLoading(false));
  }, [authHeaders, typeFilter]);

  useEffect(()=>{ fetchList(); }, [fetchList]);

  function openNew(){ navigate('/hr/employees/new'); }

  function onRowClick(id){ setSelectedId(id); }
  function onRowDblClick(id){
    const url = `/hr/employees/${id}`;
    window.open(url, '_blank', 'noopener');
  }

  function deleteEmployee(row){
    if (!row?.id) return;
    if (hrOnly && PROTECTED_USER_IDS.has(String(row.user_id||''))){
      alert('Удаление этой учетной записи доступно только администраторам.');
      return;
    }
    if (!window.confirm(`Удалить сотрудника «${row.full_name||row.user_id||row.id}» без возможности восстановления?`)) return;
    fetch(apiUrl(`/api/employees/${encodeURIComponent(row.id)}`), {
      method:'DELETE',
      headers: jsonHeaders
    })
      .then(r=> r.json().then(d=>({ ok:r.ok, data:d })))
      .then(({ok,data})=> {
        if (!ok){ alert('Ошибка удаления: '+(data?.error||'server')); return; }
        setSelectedId(s=> s===row.id? null : s);
        fetchList();
      })
      .catch(()=> alert('Сбой сети при удалении'));
  }

  const tableData = useMemo(()=>{
    const normalizeDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      parsed.setHours(0,0,0,0);
      return parsed;
    };
    const today = new Date();
    today.setHours(0,0,0,0);
    const matchesScope = (row) => {
      const isActiveContract = () => {
        const end = normalizeDate(row.contract_to);
        if (!end) return true;
        return end >= today;
      };
      if (!scopeFilter) return true;
      switch(scopeFilter){
        case 'active_gph':
          return row.contract_type === 'gph' && isActiveContract();
        case 'active_td':
          return row.contract_type === 'td' && isActiveContract();
        case 'active_all':
          return isActiveContract();
        case 'dismissed':
          return !!row.contract_to && !isActiveContract();
        default:
          return true;
      }
    };
    const scopedRows = list.filter(matchesScope).filter(filterRow);
    const pendingRows = scopedRows.filter(row => rowNeedsUrgentDocs(row));
    return {
      displayRows: showOnlyRed ? pendingRows : scopedRows,
      scopedRowCount: scopedRows.length,
      pendingRowCount: pendingRows.length
    };
  }, [list, scopeFilter, filterRow, showOnlyRed]);

  const { displayRows, scopedRowCount, pendingRowCount } = tableData;

  const exportToExcel = useCallback(() => {
    try {
      const headers = [
        'ID', 'ФИО', 'Должность', 'Объект', 'Тип', 'Гражданство', 'Регистрация до',
        '№ договора', 'Дата договора', 'Период по', 'Оформление (Бух)', 'Оформление (МВД)', 'Увольнение (Бух)', 'Увольнение (МВД)'
      ];
      const rows = displayRows.map((row) => {
        const startDocsStatus = getStatusData(row.start_send_docs, { citizenship: row.citizenship });
        const startMvdStatus = getStatusData(row.start_notify_mvd, { isMvd:true, citizenship: row.citizenship });
        const endDocsStatus = getStatusData(row.end_send_docs, { citizenship: row.citizenship, isDismissal:true, contractTo: row.contract_to });
        const endMvdStatus = getStatusData(row.end_notify_mvd, { isMvd:true, citizenship: row.citizenship, isDismissal:true, contractTo: row.contract_to });
        return [
          row.user_id || row.id || '',
          row.full_name || '',
          row.position || '',
          row.work_object || '',
          row.contract_type === 'gph' ? 'ГПХ' : 'ТД',
          row.citizenship || '',
          formatDate(row.registration_to),
          row.contract_number || '',
          formatDate(row.contract_date),
          formatDate(row.contract_to),
          startDocsStatus.text,
          startMvdStatus.text,
          endDocsStatus.text,
          endMvdStatus.text
        ];
      });

      const sheetData = [
        ['Фильтр', currentQuickFilter?.label || 'Все'],
        ['Количество строк', displayRows.length],
        ['Дата выгрузки', new Date().toLocaleString('ru-RU')],
        [],
        headers,
        ...rows
      ];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Кадры');
      const stamp = new Date().toISOString().slice(0,19).replace(/[-:T]/g,'');
      XLSX.writeFile(wb, `hr_journal_${stamp}.xlsx`);
    } catch (err) {
      alert('Не удалось выгрузить Excel: ' + (err?.message || err));
    }
  }, [displayRows, currentQuickFilter]);

  function getStatusData(value, { isMvd=false, citizenship, isDismissal=false, contractTo }){
    const base = { text:'—', color:'#475569', bg:'#f1f5f9' };
    if (isMvd && isRussianCitizen(citizenship)){
      return { text:'не требуется', color:'#15803d', bg:'#dcfce7' };
    }
    if (value === true) return { text:'Выполнено', color:'#15803d', bg:'#dcfce7' };
    if (value === false) return { text:'Не отправлено', color:'#b91c1c', bg:'#fee2e2' };
    if (isDismissal){
      let showDash = false;
      if (contractTo){
        const contractDate = new Date(contractTo);
        if (!Number.isNaN(contractDate.getTime())){
          contractDate.setDate(contractDate.getDate() + 1);
          showDash = new Date() <= contractDate;
        }
      } else {
        showDash = true;
      }
      if (showDash) return base;
      return { text:'Не отправлено', color:'#b91c1c', bg:'#fee2e2' };
    }
    return base;
  }

  function renderStatusChip(label, status){
    const shortLabel = label === 'Бух' ? 'Бух.' : (label === 'МВД' ? 'МВД' : label);
    return (
      <span style={{ ...statusChipBase, background: status.bg, color: status.color }}>
        <span style={statusLabelStyle}>{shortLabel}</span>
        <span style={statusTextStyle}>{status.text}</span>
      </span>
    );
  }

  return (
    <div style={pageWrapper}>
      {(isSuper||isAdmin||isHr) ? (
        <>
          <h2 style={{ marginTop:0 }}>Список сотрудников</h2>
          <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
            {quickFilterOptions.map(option => option.isDivider ? (
              <div key={option.key} style={filterDividerStyle} />
            ) : (
              <button
                key={option.key}
                onClick={()=> setActiveQuickFilter(option.key)}
                aria-pressed={activeQuickFilter===option.key}
                style={{
                  ...quickFilterButtonStyle,
                  ...(activeQuickFilter===option.key ? quickFilterButtonActiveStyle : {})
                }}
              >
                <span>{option.label}</span>
                {activeQuickFilter===option.key && (
                  <span style={{ ...quickFilterBadgeStyle, ...quickFilterBadgeActiveStyle }}>
                    {scopedRowCount}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={()=> setShowOnlyRed(v=>!v)}
              aria-pressed={showOnlyRed}
              style={{
                ...quickFilterButtonStyle,
                border:'1px solid #f87171',
                background: showOnlyRed ? '#b91c1c' : '#fff1f2',
                color: showOnlyRed ? '#fff' : '#991b1b',
                boxShadow: showOnlyRed ? '0 6px 18px rgba(185,28,28,0.35)' : quickFilterButtonStyle.boxShadow
              }}
            >
              <span>На отправку</span>
              <span style={{
                ...quickFilterBadgeStyle,
                ...(showOnlyRed ? quickFilterBadgeAlertActiveStyle : quickFilterBadgeAlertStyle)
              }}>
                {pendingRowCount}
              </span>
            </button>
            <div style={{ flex:1 }} />
            <button
              onClick={exportToExcel}
              style={{ padding:'8px 12px', background:'#e0f2fe', border:'1px solid #93c5fd', borderRadius:8, fontWeight:600 }}
            >
              Выгрузить в Excel
            </button>
            <button onClick={openNew} style={{ padding:'8px 12px', background:'#e0f7e9', border:'1px solid #4caf50', borderRadius:8, fontWeight:600 }}>
              Добавить сотрудника
            </button>
          </div>

          {loading ? <div>Загрузка…</div> : (
            <div style={tableWrapper} className="journal-card">
              <table style={tableStyle} className="journal-table">
                <colgroup>
                  <col style={{ width:'5%' }} />
                  <col style={{ width:'16%' }} />
                  <col style={{ width:'11%' }} />
                  <col style={{ width:'11%' }} />
                  <col style={{ width:'5%' }} />
                  <col style={{ width:'8%' }} />
                  <col style={{ width:'8%' }} />
                  <col style={{ width:'10%' }} />
                  <col style={{ width:'7%' }} />
                  <col style={{ width:'7%' }} />
                  <col style={{ width:'7%' }} />
                  <col style={{ width:'5%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background:'#f1f5f9' }}>
                    <th style={tableHeaderStyle}>ID</th>
                    <th style={tableHeaderStyle}>ФИО</th>
                    <th style={tableHeaderStyle}>Должность</th>
                    <th style={tableHeaderStyle}>Объект</th>
                    <th style={{ ...tableHeaderStyle, textAlign:'center' }}>Тип</th>
                    <th style={tableHeaderStyle}>Гражданство</th>
                    <th style={tableHeaderStyle}>Рег. до</th>
                    <th style={tableHeaderStyle}>№/дата дог.</th>
                    <th style={tableHeaderStyle}>Срок по</th>
                    <th style={{ ...tableHeaderStyle, textAlign:'center' }}>Оформление</th>
                    <th style={{ ...tableHeaderStyle, textAlign:'center' }}>Увольнение</th>
                    <th style={{ ...tableHeaderStyle, textAlign:'center' }}>Действия</th>
                  </tr>
                  <tr style={{ background:'#f8fafc' }}>
                    <th><input value={filters.user_id} onChange={e=>handleFilterChange('user_id',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.full_name} onChange={e=>handleFilterChange('full_name',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.position} onChange={e=>handleFilterChange('position',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.work_object} onChange={e=>handleFilterChange('work_object',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.contract_type} onChange={e=>handleFilterChange('contract_type',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.citizenship} onChange={e=>handleFilterChange('citizenship',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th><input value={filters.registration_to} onChange={e=>handleFilterChange('registration_to',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th>
                      <input value={filters.contract_number} onChange={e=>handleFilterChange('contract_number',e.target.value)} placeholder="№..." style={{ ...filterInputStyle, width:'48%', marginRight:2 }} />
                      <input value={filters.contract_date} onChange={e=>handleFilterChange('contract_date',e.target.value)} placeholder="Дата..." style={{ ...filterInputStyle, width:'48%' }} />
                    </th>
                    <th><input value={filters.contract_to} onChange={e=>handleFilterChange('contract_to',e.target.value)} placeholder="Поиск..." style={filterInputStyle} /></th>
                    <th></th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, idx) => {
                    const isSelected = selectedId===row.id;
                    const zebra = idx % 2 === 1;
                    const contractEndDate = row.contract_to ? (() => { const d = new Date(row.contract_to); d.setHours(0,0,0,0); return d; })() : null;
                    const isDismissed = !!contractEndDate && contractEndDate < new Date(new Date().setHours(0,0,0,0));
                    const rowBackground = isSelected ? '#e0f2fe' : isDismissed ? '#fff1f2' : (zebra ? '#f8fafc' : '#ffffff');
                    const dividerColor = isSelected ? '#1d4ed8' : isDismissed ? '#fca5a5' : '#cbd5f5';
                    const protectedRow = PROTECTED_USER_IDS.has(String(row.user_id||''));
                    const startDocsStatus = getStatusData(row.start_send_docs, { citizenship: row.citizenship });
                    const startMvdStatus = getStatusData(row.start_notify_mvd, { isMvd:true, citizenship: row.citizenship });
                    const endDocsStatus = getStatusData(row.end_send_docs, { citizenship: row.citizenship, isDismissal:true, contractTo: row.contract_to });
                    const endMvdStatus = getStatusData(row.end_notify_mvd, { isMvd:true, citizenship: row.citizenship, isDismissal:true, contractTo: row.contract_to });
                    return (
                      <tr key={row.id}
                        onClick={()=> onRowClick(row.id)}
                        onDoubleClick={()=> onRowDblClick(row.id)}
                        style={{
                          ...rowBaseStyle,
                          background: rowBackground,
                          cursor:'pointer',
                          borderBottom:`2px solid ${dividerColor}`
                        }}>
                        <td style={{ ...cellStyle, fontWeight:600, fontFamily:'monospace', color:'#1e3a8a' }}>
                          {row.id ? (
                            <Link to={`/hr/employees/${row.id}`} target='_blank' rel='noreferrer' style={journalLinkStyle}>
                              {row.user_id || '—'}
                            </Link>
                          ) : (row.user_id || '—')}
                        </td>
                        <td style={cellStyle}>{row.full_name||'—'}</td>
                        <td style={{ ...cellStyle, color:'#475569' }}>{row.position||'—'}</td>
                        <td style={cellStyle}>{row.work_object||'—'}</td>
                        <td style={{ ...cellStyle, textAlign:'center' }}>{row.contract_type==='gph'?'ГПХ':'ТД'}</td>
                        <td style={cellStyle}>{row.citizenship||'—'}</td>
                        <td style={cellStyle}>{formatDate(row.registration_to)}</td>
                        <td style={cellStyle}><span>{row.contract_number||'—'} / {formatDate(row.contract_date)}</span></td>
                        <td style={cellStyle}>{formatDate(row.contract_to)}</td>
                        <td style={{ ...cellStyle, textAlign:'center' }}>
                          <div style={statusCellStyle}>
                            {renderStatusChip('Бух', startDocsStatus)}
                            {renderStatusChip('МВД', startMvdStatus)}
                          </div>
                        </td>
                        <td style={{ ...cellStyle, textAlign:'center' }}>
                          <div style={statusCellStyle}>
                            {renderStatusChip('Бух', endDocsStatus)}
                            {renderStatusChip('МВД', endMvdStatus)}
                          </div>
                        </td>
                        <td style={{ ...cellStyle, textAlign:'center' }}>
                          <button
                            onClick={(e)=>{ e.stopPropagation(); deleteEmployee(row); }}
                            disabled={hrOnly && protectedRow}
                            style={{
                              padding:'4px 8px',
                              background: hrOnly && protectedRow ? '#f8fafc' : '#fee2e2',
                              border:'1px solid #ef4444',
                              borderRadius:6,
                              color: hrOnly && protectedRow ? '#94a3b8' : '#991b1b',
                              fontSize:12,
                              cursor: hrOnly && protectedRow ? 'not-allowed' : 'pointer'
                            }}
                          >Удалить</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div>Недостаточно прав</div>
      )}
    </div>
  );
}

function sortEmployeesById(list){
  const getTimestamp = (row) => {
    const dateSources = [row?.updatedAt, row?.createdAt, row?.contract_date];
    for (const source of dateSources) {
      if (!source) continue;
      const ts = Date.parse(source);
      if (!Number.isNaN(ts)) return ts;
    }
    return Number(row?.id) || 0;
  };
  return [...list].sort((a,b)=>{
    const tsA = getTimestamp(a);
    const tsB = getTimestamp(b);
    if (tsA !== tsB) return tsB - tsA; // новые первыми
    const idA = Number(a?.id) || 0;
    const idB = Number(b?.id) || 0;
    if (idA !== idB) return idB - idA;
    const userA = String(a?.user_id || '').trim();
    const userB = String(b?.user_id || '').trim();
    return userB.localeCompare(userA, 'ru', { sensitivity:'base', numeric:true });
  });
}
